import React, { useContext, useEffect, useState } from "react";
import GroupsInfo from "./GroupsInfo";
import OffcanvasFooter from "../../Helpers/Offcanvasfooter/OffcanvasFooter";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
	useAddGroupsMutation,
	useEditGroupsMutation,
} from "../../../Redux/service/Groups/Groups";
import { toast } from "react-toastify";
import { popupcontext } from "../../../context/Popupscontext";
import Loader from "../../Helpers/Loader/Loader";
import trans, { isRTL } from "../../../utils";
import {
	SpeedInputValue,
	SpeedValue,
	VolumeInputValue,
	VolumeValue,
} from "../../../JsHelpers/PlatformUnits";
import { useSelector } from "react-redux";

export default function GroupsBody({ refetch, Editvalues }) {
	const [submitAttempt, setSubmitAttempt] = useState(false);
	const inf = useSelector((state) => state.users.user);
	let { handleClose, isEditing, currentGroupId } = useContext(popupcontext);
	const sqlInjectionPattern =
		/('|--|;|\/\*|\*\/|xp_|exec|union|select|insert|update|delete|drop|alter|create|truncate)/i;
	const forbiddenPattern = /[&"'\/<>%+{}\[\];$]/; // block these characters
	const consecutiveSpaces = /\s{2,}/;
	const onlyNumbers = /^\d+$/;
	const [AddGroup, { isLoading: addLoading }] = useAddGroupsMutation();
	const [EditGroup, { isLoading: editLoading }] = useEditGroupsMutation();
	let validSchema = Yup.object({
		name: Yup.string()
			.transform((value) => value?.trim())
			.required("Name is required")
			.matches(
				/^[\u0621-\u064A\u0660-\u0669a-zA-Z0-9\s]*$/,
				trans("Only letters, numbers, and spaces are allowed")
			)
			.test(
				"no-sql-injection",
				trans("Input contains potentially unfavor characters"),
				(value) => !sqlInjectionPattern.test(value || "")
			)
			.test(
				"not-empty-or-spaces",
				trans("Name cannot be just whitespace"),
				(value) => !!value && value.trim().length > 0
			)
			.test(
				"no-consecutive-spaces",
				trans("Name cannot contain consecutive spaces"),
				(value) => !consecutiveSpaces.test(value || "")
			)
			.test(
				"not-only-numbers",
				trans("Name cannot contain only numbers"),
				(value) => !onlyNumbers.test(value || "")
			)
			.test(
				"no-forbidden-characters",
				trans("Name contains invalid characters"),
				(value) => !forbiddenPattern.test(value || "")
			),

		attributes: Yup.object({
			speedLimit: Yup.number()
				.typeError(trans("Speed Limit accepts numbers only"))
				.integer(trans("Speed Limit must be a whole number"))
				.min(1, trans("Speed Limit must be a positive number"))
				.nullable(),
			fuelIncreaseThreshold: Yup.number()
				.typeError(trans("Fuel Increase Threshold accepts numbers only"))
				.integer(trans("Fuel Increase Threshold must be a whole number"))
				.min(1, trans("Fuel Increase Threshold must be a positive number"))
				.nullable(),
			fuelDropThreshold: Yup.number()
				.typeError(trans("Fuel Drop Threshold accepts numbers only"))
				.integer(trans("Fuel Drop Threshold must be a whole number"))
				.min(1, trans("Fuel Drop Threshold must be a positive number"))
				.nullable(),
			code: Yup.string()
				.max(10, "Code maximum 10 char")
				.matches(
					/^[\u0621-\u064A\u0660-\u0669a-zA-Z0-9\s]*$/,
					trans("Only letters, numbers, and spaces are allowed")
				)
				.test(
					"no-sql-injection",
					trans("Input contains potentially unfavor characters"),
					(value) => !sqlInjectionPattern.test(value || "")
				)
				.test(
					"no-consecutive-spaces",
					trans("Name cannot contain consecutive spaces"),
					(value) => !consecutiveSpaces.test(value || "")
				)
				.test(
					"no-forbidden-characters",
					trans("Name contains invalid characters"),
					(value) => !forbiddenPattern.test(value || "")
				)
				.nullable(),
		}),
	});

	const initialValues = {
		name: "",
		groupId: "",
		attributes: {
			speedLimit: null,
			fuelDropThreshold: null,
			fuelIncreaseThreshold: null,
			code: null,
		},
	};

	const addGroup = useFormik({
		initialValues: initialValues,
		validationSchema: validSchema,
		onSubmit: (val) => {
			// Wrap speedLimit
			console.log("Val", val);
			const updatedVal = {
				...val,
				attributes: {
					...val.attributes,
					speedLimit: SpeedInputValue(
						val.attributes?.speedLimit,
						inf.attributes?.speedUnit || "kn"
					),
					fuelIncreaseThreshold: VolumeInputValue(
						val.attributes?.fuelIncreaseThreshold,
						inf.attributes?.volumeUnit || "ltr"
					),
					fuelDropThreshold: VolumeInputValue(
						val.attributes?.fuelDropThreshold,
						inf.attributes?.volumeUnit || "ltr"
					),
				},
			};

			if (!isEditing) {
				AddGroup(updatedVal)
					.unwrap()
					.then(() => {
						toast.success(trans("Group added successfully"), {
							rtl: localStorage.getItem("lang") === "ar",
						});
						refetch();
						handleClose("form");
					})
					.catch((err) =>
						toast.error(trans("Failed to add group"), {
							autoClose: 800,
							rtl: localStorage.getItem("lang") === "ar" ? true : false,
						})
					);
			} else {
				console.log("Updated Val", updatedVal);
				EditGroup({ val: updatedVal, id: currentGroupId })
					.unwrap()
					.then(() => {
						toast.success(trans("Group edited successfully"), {
							autoClose: 800,
							rtl: localStorage.getItem("lang") === "ar" ? true : false,
						});
						refetch();
						handleClose("form");
					})
					.catch((err) =>
						toast.error(trans("Failed to edit group"), {
							autoClose: 800,
							rtl: localStorage.getItem("lang") === "ar" ? true : false,
						})
					);
			}
		},
	});

	useEffect(() => {
		if (isEditing && Editvalues) {
			const updatedEditValues = {
				...Editvalues,
				attributes: {
					...Editvalues.attributes,
					speedLimit: SpeedValue(
						Editvalues.attributes?.speedLimit,
						inf.attributes?.speedUnit || "kn"
					),
					fuelIncreaseThreshold: VolumeValue(
						Editvalues.attributes?.fuelIncreaseThreshold,
						inf.attributes?.volumeUnit || "ltr"
					),
					fuelDropThreshold: VolumeValue(
						Editvalues.attributes?.fuelDropThreshold,
						inf.attributes?.volumeUnit || "ltr"
					),
				},
			};
			console.log("AAAA", updatedEditValues);
			addGroup.setValues(updatedEditValues);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [Editvalues, inf.attributes?.speedUnit, inf.attributes?.volumeUnit]);

	const handleSubmit = (e) => {
		setSubmitAttempt(true);
		addGroup.handleSubmit(e);
	};

	return (
		<form onSubmit={handleSubmit} dir={!isRTL ? "ltr" : "rtl"}>
			{addLoading || editLoading ? (
				<div className="loader-container">
					<Loader />
				</div>
			) : (
				<GroupsInfo addData={addGroup} />
			)}
			<OffcanvasFooter addData={addGroup} submitAttempt={submitAttempt} />
		</form>
	);
}
